import React, { useState } from 'react';
import { Save, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAppSettings } from '../contexts/AppSettingsContext';

const Settings = () => {
  const { appSettings, submitAppSettings, loading } = useAppSettings();
  const [editingSettings, setEditingSettings] = useState(null);
  
  // Check if editing values actually differ from original appSettings
  const hasChanges = editingSettings && JSON.stringify(editingSettings) !== JSON.stringify(appSettings);
  
  // Current settings to display (either original appSettings or being edited)
  const currentSettings = editingSettings || appSettings || {
    defaultPercentageDifference: 1,
    defaultMinimalVolume: 1000000,
    volumeSteps: 500000,
    quickBuyDefaultVolume: 1000000,
    defaultTradeVolumeIDR: 1000000,
    jackpotPercentage: 5,
  };
  
  // Format number with dots for thousands
  const formatNumberInput = (value) => {
    // Remove all non-digit characters
    const numbers = value.toString().replace(/\D/g, '');
    // Add dots for thousands
    return numbers.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  };

  // Parse formatted number back to plain number
  const parseFormattedNumber = (value) => {
    return value.toString().replace(/\./g, '');
  };

  const handleChange = (field, value) => {
    // Start editing mode if not already editing
    if (!editingSettings) {
      setEditingSettings({ ...appSettings });
    }
    
    // Check if this is a currency/volume field that needs formatting
    const currencyFields = ['defaultMinimalVolume', 'volumeSteps', 'quickBuyDefaultVolume', 'defaultTradeVolumeIDR'];
    const percentageFields = ['defaultPercentageDifference', 'jackpotPercentage'];
    
    let newValue;
    if (currencyFields.includes(field)) {
      // For currency fields, parse the formatted value
      const plainNumber = parseFormattedNumber(value);
      newValue = parseInt(plainNumber) || 0;
    } else if (percentageFields.includes(field)) {
      // For percentage, allow decimals
      const cleanValue = value.toString().replace(/[^0-9.]/g, '');
      const parts = cleanValue.split('.');
      const formattedValue = parts[0] + (parts.length > 1 ? '.' + parts[1] : '');
      newValue = parseFloat(formattedValue) || 0;
    } else {
      // For other fields, just parse as number
      newValue = parseInt(value) || 0;
    }
    
    setEditingSettings(prev => ({ ...prev, [field]: newValue }));
  };

  const handleSave = async () => {
    try {
      // Save using AppSettings API
      await submitAppSettings(editingSettings);
      toast.success('Settings saved successfully!');
      setEditingSettings(null);
    } catch (error) {
    
      toast.error('Failed to save settings');
    }
  };

  const handleReset = async () => {
    const defaultSettings = {
      defaultPercentageDifference: 1,
      defaultMinimalVolume: 1000000,
      volumeSteps: 500000,
      quickBuyDefaultVolume: 1000000,
      defaultTradeVolumeIDR: 1000000,
      jackpotPercentage: 5,
    };
    
    try {
      // Save default settings using AppSettings API
      await submitAppSettings(defaultSettings);
      toast.success('Settings reset to defaults');
      setEditingSettings(null);
    } catch (error) {
      toast.error('Failed to save reset settings');
    }
  };

  const handleCancel = () => {
    setEditingSettings(null);
  };


  return (
    <div className="space-y-6 max-w-4xl">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Settings</h1>
        <p className="text-gray-400">Configure your trading preferences and parameters</p>
      </div>

      {/* Settings Form */}
      <div className="glass-card p-6">
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Default Percentage Difference (%)
            </label>
            <input
              type="text"
              value={currentSettings.defaultPercentageDifference || ''}
              onChange={(e) => handleChange('defaultPercentageDifference', e.target.value)}
              className="input-field"
            />
            <p className="mt-1 text-xs text-gray-500">Minimum profit percentage to show opportunities</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Default Minimal Volume
            </label>
            <input
              type="text"
              value={formatNumberInput(currentSettings.defaultMinimalVolume || 0)}
              onChange={(e) => handleChange('defaultMinimalVolume', e.target.value)}
              className="input-field"
              placeholder="1.000.000"
            />
            <p className="mt-1 text-xs text-gray-500">Minimum volume threshold for trading pairs</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Volume Steps
            </label>
            <input
              type="text"
              value={formatNumberInput(currentSettings.volumeSteps || 0)}
              onChange={(e) => handleChange('volumeSteps', e.target.value)}
              className="input-field"
              placeholder="1.000.000"
            />
            <p className="mt-1 text-xs text-gray-500">Volume steps for the volume-related fields</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Quick Buy Default Volume
            </label>
            <input
              type="text"
              value={formatNumberInput(currentSettings.quickBuyDefaultVolume || 0)}
              onChange={(e) => handleChange('quickBuyDefaultVolume', e.target.value)}
              className="input-field"
              placeholder="1.000.000"
            />
            <p className="mt-1 text-xs text-gray-500">Default volume for quick buy (IDR)</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Default Trade Volume (IDR)
            </label>
            <input
              type="text"
              value={formatNumberInput(currentSettings.defaultTradeVolumeIDR || 0)}
              onChange={(e) => handleChange('defaultTradeVolumeIDR', e.target.value)}
              className="input-field"
              placeholder="1.000.000"
            />
            <p className="mt-1 text-xs text-gray-500">Default volume for trading operations</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Jackpot Percentage (%)
            </label>
            <input
              type="text"
              value={currentSettings.jackpotPercentage || ''}
              onChange={(e) => handleChange('jackpotPercentage', e.target.value)}
              className="input-field"
            />
            <p className="mt-1 text-xs text-gray-500">Minimum profit percentage to show opportunities as jackpot</p>
          </div>
        </div>

        <div className="flex items-center justify-between mt-8 pt-6 border-t border-dark-border">
          <button
            onClick={handleReset}
            className="btn-secondary"
            disabled={loading}
          >
            Reset to Defaults
          </button>
          <div className="flex space-x-3">
            {hasChanges && (
              <button
                onClick={handleCancel}
                className="btn-secondary"
              >
                Cancel
              </button>
            )}
            <button
              onClick={handleSave}
              disabled={!hasChanges || loading}
              className="btn-primary flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="h-4 w-4 mr-2" />
              Save Settings
            </button>
          </div>
        </div>
      </div>

      {/* Info Box */}
      <div className="glass-card p-6 bg-yellow-500/5 border-yellow-500/20">
        <div className="flex items-start space-x-3">
          <AlertCircle className="h-5 w-5 text-yellow-500 mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold text-white mb-1">Trading Settings Guide</h3>
            <p className="text-sm text-gray-400">
              These settings affect how arbitrage opportunities are calculated and displayed. 
              Lower percentage differences will show more opportunities but with smaller profit margins. 
              Adjust volume thresholds based on your trading capital and risk tolerance.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;